import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircleOutline,
  RadioButtonUnchecked,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useChangePasswordMutation } from "@/app/features/auth/authApiSlice";
import { selectUser } from "@/app/features/auth/authSlice";
import { delftBlue, flame, neutral } from "@/theme/colors";
import useSnackbar from "@/hooks/use-snackbar";
import {
  ADMIN_PASSWORD_REGEX,
  ADMIN_PASSWORD_REGEX_MESSAGE,
  ADMIN_PASSWORD_SPECIAL_CHAR_REGEX,
} from "@/constants/password";

const newPasswordRules = Yup.string()
  .min(8, "Length should be 8-50 characters")
  .max(50, "Length should be 8-50 characters")
  .matches(ADMIN_PASSWORD_REGEX, ADMIN_PASSWORD_REGEX_MESSAGE);

const validationSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: newPasswordRules.required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords don't match")
    .required("Please confirm your new password"),
});

function PasswordField({ label, name, formik, show, onToggle, autoComplete }) {
  const touched = formik.touched[name];
  const error = formik.errors[name];
  const hasError = Boolean(touched && error);

  return (
    <TextField
      fullWidth
      label={label}
      name={name}
      type={show ? "text" : "password"}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={hasError}
      helperText={hasError ? error : " "}
      autoComplete={autoComplete}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={show ? "Hide password" : "Show password"}
              onClick={onToggle}
              edge="end"
            >
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          backgroundColor: neutral[50],
        },
      }}
    />
  );
}

function RequirementItem({ met, label }) {
  return (
    <ListItem disableGutters sx={{ py: 0.25 }}>
      <ListItemIcon sx={{ minWidth: 28 }}>
        {met ? (
          <CheckCircleOutline sx={{ fontSize: 18, color: "success.main" }} />
        ) : (
          <RadioButtonUnchecked sx={{ fontSize: 18, color: neutral[400] }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          variant: "body2",
          color: met ? "text.primary" : "text.secondary",
        }}
      />
    </ListItem>
  );
}

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { showSnackbar, snackbar } = useSnackbar();
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values, helpers) => {
      helpers.setStatus(null);
      try {
        await changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        }).unwrap();

        showSnackbar({
          message: "Password changed successfully",
          type: "success",
        });
        helpers.resetForm();
        navigate("/");
      } catch (err) {
        const message =
          err?.data?.message ||
          err?.data?.error ||
          "Failed to change password. Please try again.";
        helpers.setStatus({ error: message });
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const newPwd = formik.values.newPassword;
  const requirements = [
    {
      met: newPwd.length >= 8 && newPwd.length <= 50,
      label: "Length should be 8-50 characters",
    },
    { met: /[A-Z]/.test(newPwd), label: "One uppercase letter" },
    { met: /[a-z]/.test(newPwd), label: "One lowercase letter" },
    { met: /\d/.test(newPwd), label: "One number" },
    {
      met: ADMIN_PASSWORD_SPECIAL_CHAR_REGEX.test(newPwd),
      label: "One special character",
    },
    {
      met:
        newPwd.length > 0 &&
        formik.values.currentPassword.length > 0 &&
        newPwd !== formik.values.currentPassword,
      label: "Different from current password",
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        maxWidth: 720,
        mx: "auto",
        width: "100%",
      }}
    >
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, color: delftBlue[500], textTransform: "none" }}
      >
        Back
      </Button>

      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ color: delftBlue[500], fontWeight: 600 }}
        >
          Change password
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Update the password for{" "}
          <Typography component="span" fontWeight={600} color="text.primary">
            {user?.name || user?.email || "your account"}
          </Typography>
          . You will stay signed in after saving.
        </Typography>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 4 },
          borderRadius: 3,
          border: `1px solid ${neutral[200]}`,
          boxShadow: "0 4px 24px rgba(22, 45, 79, 0.06)",
        }}
      >
        {formik.status?.error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {formik.status.error}
          </Alert>
        )}

        <Box component="form" noValidate onSubmit={formik.handleSubmit}>
          <Stack spacing={2}>
            <PasswordField
              label="Current password"
              name="currentPassword"
              formik={formik}
              show={showCurrent}
              onToggle={() => setShowCurrent((v) => !v)}
              autoComplete="current-password"
            />

            <PasswordField
              label="New password"
              name="newPassword"
              formik={formik}
              show={showNew}
              onToggle={() => setShowNew((v) => !v)}
              autoComplete="new-password"
            />

            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: delftBlue[50],
                border: `1px solid ${delftBlue[100]}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: delftBlue[600], mb: 0.5 }}
              >
                New password requirements
              </Typography>
              <List dense disablePadding>
                {requirements.map((req) => (
                  <RequirementItem
                    key={req.label}
                    met={req.met}
                    label={req.label}
                  />
                ))}
              </List>
            </Box>

            <PasswordField
              label="Confirm new password"
              name="confirmPassword"
              formik={formik}
              show={showConfirm}
              onToggle={() => setShowConfirm((v) => !v)}
              autoComplete="new-password"
            />

            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={2}
              justifyContent="flex-end"
              sx={{ pt: 1 }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  borderColor: neutral[300],
                  color: delftBlue[500],
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || formik.isSubmitting}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  bgcolor: flame[500],
                  "&:hover": { bgcolor: flame[600] },
                  px: 4,
                }}
              >
                {isLoading ? "Saving…" : "Save new password"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        Forgot your password? Use{" "}
        <Button
          variant="text"
          size="small"
          onClick={() => navigate("/forgot-password")}
          sx={{
            textTransform: "none",
            p: 0,
            minWidth: 0,
            verticalAlign: "baseline",
          }}
        >
          reset password
        </Button>{" "}
        from the sign-in page (you will be signed out).
      </Typography>

      {snackbar}
    </Box>
  );
};

export default ChangePasswordPage;
